import { useMemo } from 'react';
import type { AvetmissResponse, NatFileType } from '@duke-hq/avetmiss/NATTypes';
import { NatReportTitles } from '@duke-hq/avetmiss/NATRules';
import type { MissingReference } from '@duke-hq/avetmiss/NATCrossFileRules';
import {
  validateCrossFileReferences,
  type CrossFileValidationResult,
} from '@duke-hq/avetmiss/crossFileValidation/crossFileValidation';

function isNatFileType(value: string): value is NatFileType {
  return Object.keys(NatReportTitles).includes(value);
}

export interface UseCrossFileValidationProps {
  natResponses: Partial<Record<NatFileType, AvetmissResponse>>;
  enabled?: boolean;
}

export interface UseCrossFileValidationReturn {
  validationResult: CrossFileValidationResult | null;
  crossFileOnlyReferences: MissingReference[];
  isValidating: boolean;
  hasValidationErrors: boolean;
  hasValidationWarnings: boolean;
  getErrorsForFile: (fileType: NatFileType) => string[];
  getWarningsForFile: (fileType: NatFileType) => string[];
}

export function useCrossFileValidation({
  natResponses,
  enabled = true,
}: UseCrossFileValidationProps): UseCrossFileValidationReturn {
  const validationResult = useMemo(() => {
    if (!enabled) {
      return null;
    }

    const validResponses = new Map<NatFileType, AvetmissResponse>();

    Object.entries(natResponses).forEach(([fileType, response]) => {
      if (response != null && isNatFileType(fileType)) {
        validResponses.set(fileType, response);
      }
    });

    if (validResponses.size === 0) {
      return null;
    }

    try {
      const crossFileResult = validateCrossFileReferences(validResponses);

      // Add field validation errors from each NAT file
      const fieldValidationErrors = Array.from(validResponses.entries()).flatMap(
        ([fileType, response]) => {
          return response.errors.map((error) => ({
            sourceFile: fileType,
            targetFile: fileType,
            rule: error.field,
            missingKey: error.message,
            severity: error.severity,
            sourceRowIndex: error.rowIndex ?? -1,
            sourceRowData:
              error.rowIndex !== undefined && error.rowIndex >= 0
                ? (response.data.rows[error.rowIndex] ?? [])
                : [],
          }));
        },
      );

      // Combine cross-file errors with field validation errors
      const combinedErrors = [...crossFileResult.missingReferences, ...fieldValidationErrors];
      const totalErrors =
        crossFileResult.validationSummary.totalErrors +
        fieldValidationErrors.filter((e) => e.severity === 'error').length;
      const totalWarnings =
        crossFileResult.validationSummary.totalWarnings +
        fieldValidationErrors.filter((e) => e.severity === 'warning').length;

      return {
        isValid: crossFileResult.isValid && totalErrors === 0,
        missingReferences: combinedErrors,
        orphanedRecords: crossFileResult.orphanedRecords,
        validationSummary: {
          totalErrors,
          totalWarnings,
          filesMissing: crossFileResult.validationSummary.filesMissing,
          rulesApplied: crossFileResult.validationSummary.rulesApplied,
        },
      };
    } catch (error) {
      console.error('Cross-file validation failed:', error);
      return null;
    }
  }, [natResponses, enabled]);

  const getErrorsForFile = useMemo(() => {
    return (fileType: NatFileType): string[] => {
      if (!validationResult) {
        return [];
      }

      return validationResult.missingReferences
        .filter(
          (ref) =>
            (ref.sourceFile === fileType || ref.targetFile === fileType) &&
            ref.severity === 'error',
        )
        .map((ref) => `${ref.rule}: Missing reference "${ref.missingKey}"`);
    };
  }, [validationResult]);

  const getWarningsForFile = useMemo(() => {
    return (fileType: NatFileType): string[] => {
      if (!validationResult) {
        return [];
      }

      return validationResult.missingReferences
        .filter(
          (ref) =>
            (ref.sourceFile === fileType || ref.targetFile === fileType) &&
            ref.severity === 'warning',
        )
        .map((ref) => `${ref.rule}: Missing reference "${ref.missingKey}"`);
    };
  }, [validationResult]);

  const crossFileOnlyReferences = useMemo<MissingReference[]>(() => {
    if (!enabled) {
      return [];
    }
    const validResponses = new Map<NatFileType, AvetmissResponse>();
    Object.entries(natResponses).forEach(([fileType, response]) => {
      if (response != null && isNatFileType(fileType)) {
        validResponses.set(fileType, response);
      }
    });
    if (validResponses.size === 0) {
      return [];
    }
    try {
      return validateCrossFileReferences(validResponses).missingReferences;
    } catch {
      return [];
    }
  }, [natResponses, enabled]);

  return {
    validationResult,
    crossFileOnlyReferences,
    isValidating: false,
    hasValidationErrors: validationResult
      ? validationResult.validationSummary.totalErrors > 0
      : false,
    hasValidationWarnings: validationResult
      ? validationResult.validationSummary.totalWarnings > 0
      : false,
    getErrorsForFile,
    getWarningsForFile,
  };
}
